/* Load Shopify-rendered product media and catalog data into the gifting page.
   Liquid cannot read arbitrary URL query parameters; no browser-created prices/data. */
(() => {
  'use strict';
  const scripts = new Map();
  function loadScript(source) {
    const url = new URL(source, location.href).href;
    if (scripts.has(url)) return scripts.get(url);
    // Scripts already present in the original page are defer-loaded before this entry.
    if (Array.from(document.scripts).some(script => script.src === url)) return Promise.resolve();
    const task = new Promise((resolve, reject) => {
      const script = document.createElement('script'); script.src = url; script.async = false;
      script.onload = resolve; script.onerror = () => { script.remove(); reject(new Error('A gifting component could not load. Please refresh and try again.')); };
      document.head.append(script);
    });
    scripts.set(url, task); return task;
  }
  const baseCatalogs = new WeakMap();
  const draftKey = 'pt-gift-fabric-draft';
  function selectorURL() {
    const url = new URL(location.href);
    url.searchParams.delete('fabric');
    url.searchParams.delete('occasion');
    url.searchParams.delete('page');
    return url;
  }
  async function loadCatalog(root, params) {
    const catalog = root.querySelector('[data-gift-fabric-catalog]');
    const requestId = String(Number(root.dataset.catalogRequest || 0) + 1);
    root.dataset.catalogRequest = requestId;
    catalog.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(root.dataset.rootUrl + 'collections/fabric-catalog', location.origin);
      for (const [key, value] of params) if (key.startsWith('filter.') || key === 'page') url.searchParams.append(key, value);
      url.searchParams.set('section_id', 'pt-gift-fabric-selector');
      if (!baseCatalogs.has(root)) {
        const baseURL = new URL(url); baseURL.search = '?section_id=pt-gift-fabric-selector';
        baseCatalogs.set(root, fetch(baseURL, {credentials:'same-origin'}).then(response => {
          if (!response.ok) throw new Error('catalog');
          return response.text();
        }).catch(error => { baseCatalogs.delete(root); throw error; }));
      }
      const baseHTML = await baseCatalogs.get(root);
      let html = baseHTML;
      if (Array.from(params.keys()).some(key => key.startsWith('filter.') || key === 'page')) {
        const response = await fetch(url, {credentials:'same-origin'});
        if (!response.ok) throw new Error('catalog');
        html = await response.text();
      }
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const base = new DOMParser().parseFromString(baseHTML, 'text/html');
      parsed.querySelectorAll('select').forEach(select => {
        const original = Array.from(base.querySelectorAll('select')).find(item => item.name === select.name);
        if (original) select.replaceChildren(...Array.from(original.options).map(option => option.cloneNode(true)));
        select.value = params.get(select.name) || '';
      });
      const results = parsed.querySelector('[data-gift-fabric-results]');
      if (!results) throw new Error('catalog');
      if (root.dataset.catalogRequest !== requestId) return;
      catalog.replaceChildren(document.importNode(results, true));
      catalog.querySelectorAll('[data-gift-select-fabric]').forEach(link => {
        const target = selectorURL();
        target.searchParams.set('fabric', link.dataset.giftSelectFabric);
        target.searchParams.delete('page');
        for (const key of Array.from(target.searchParams.keys())) if (key.startsWith('filter.')) target.searchParams.delete(key);
        link.href = target.href;
      });
    } catch (_) {
      if (root.dataset.catalogRequest !== requestId) return;
      catalog.replaceChildren();
      const message = document.createElement('p');
      message.textContent = 'We couldn’t load the fabrics. Please try again.';
      const retry = document.createElement('button');
      retry.className = 'btn btn--primary'; retry.textContent = 'Try again';
      retry.addEventListener('click', () => loadCatalog(root, params));
      catalog.append(message, retry);
    } finally { if (root.dataset.catalogRequest === requestId) catalog.removeAttribute('aria-busy'); }
  }
  function setupSelector(root) {
    const selector = root.querySelector('[data-gift-fabric-selector]');
    function update(params) {
      const url = selectorURL();
      for (const key of Array.from(url.searchParams.keys())) if (key.startsWith('filter.') || key === 'page') url.searchParams.delete(key);
      for (const [key, value] of params) if (key.startsWith('filter.') || key === 'page') url.searchParams.append(key, value);
      history.replaceState(null, '', url);
      loadCatalog(root, params);
    }
    selector.addEventListener('submit', event => event.preventDefault());
    selector.addEventListener('change', event => {
      const form = event.target.closest('[data-gift-fabric-filters]');
      if (!form) return;
      const params = new URLSearchParams();
      if (event.target.value) params.set(event.target.name, event.target.value);
      update(params);
    });
    selector.addEventListener('click', event => {
      if (event.target.closest('[data-gift-fabric-clear]')) { update(new URLSearchParams()); return; }
      const link = event.target.closest('[data-gift-fabric-pagination] a');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      update(new URL(link.href).searchParams);
      selector.scrollIntoView({behavior: 'smooth', block: 'start'});
    });
    loadCatalog(root, new URLSearchParams(location.search));
  }
  function saveDraft(root) {
    const fields = Array.from(root.querySelectorAll('[data-gift-note], [data-gift-choice], [data-gift-timing]'))
      .filter(field => field.type !== 'radio' || field.checked)
      .map(field => ({note:field.dataset.giftNote, choice:field.hasAttribute('data-gift-choice'), timing:field.hasAttribute('data-gift-timing'), value:field.value}));
    try { sessionStorage.setItem(draftKey, JSON.stringify(fields)); } catch (_) {}
  }
  function restoreDraft(root) {
    let fields = [];
    try { fields = JSON.parse(sessionStorage.getItem(draftKey) || '[]'); } catch (_) {}
    if (!Array.isArray(fields)) return;
    const preset = new URLSearchParams(location.search).get('preset');
    if (['couple','women','men','girls','boys','parent-kids','family'].includes(preset)) fields = fields.filter(field => !field.choice);
    for (const saved of fields) {
      const field = Array.from(root.querySelectorAll('[data-gift-note], [data-gift-choice], [data-gift-timing]')).find(field =>
        saved.choice ? field.hasAttribute('data-gift-choice') && field.value === saved.value :
        saved.timing ? field.hasAttribute('data-gift-timing') && field.value === saved.value :
        field.dataset.giftNote === saved.note && (field.type !== 'radio' || field.value === saved.value));
      if (!field) continue;
      if (field.type === 'radio') field.checked = true; else field.value = saved.value;
      field.dispatchEvent(new Event('input', {bubbles:true}));
      field.dispatchEvent(new Event('change', {bubbles:true}));
    }
    try { sessionStorage.removeItem(draftKey); } catch (_) {}
  }
  async function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const handle = new URLSearchParams(location.search).get('fabric');
    root.addEventListener('click', event => {
      const link = event.target.closest('[data-gift-return-selector]');
      if (link) { event.preventDefault(); saveDraft(root); window.location.assign(selectorURL().href); }
    });
    if (!handle) { setupSelector(root); return; }
    const status = root.querySelector('[data-gifting-status]');
    const mount = root.querySelector('[data-gifting-mount]');
    const message = status.querySelector('p');
    root.querySelector('[data-gift-fabric-selector]').hidden = true;
    status.hidden = false;
    if (!/^[a-z0-9][a-z0-9-]*$/.test(handle)) { message.textContent = 'Choose a fabric from our catalog to start your gift.'; return; }
    // Keep the centred opening screen until all builder components are ready.
    root.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(root.dataset.rootUrl + 'products/' + encodeURIComponent(handle), location.origin);
      url.searchParams.set('section_id', root.dataset.giftingRenderSection);
      const response = await fetch(url, {credentials:'same-origin'});
      if (!response.ok) throw new Error('This fabric could not be loaded. Please choose another fabric.');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = parsed.querySelector('[data-gifting-product]');
      if (!content || !content.querySelector('[data-occasion-builder]')) throw new Error('This fabric is unavailable for gifting. Please choose another fabric or contact us.');
      // Section rendering may place dialog siblings outside the product wrapper.
      // Import the catalog-bearing guides explicitly before builder initialization.
      for (const id of ['modal-pt-size-guide', 'modal-pt-style-guide']) {
        const guide = parsed.getElementById(id);
        if (guide && !content.contains(guide)) content.append(guide);
      }
      const sources = Array.from(parsed.querySelectorAll('script[src]')).map(script => script.getAttribute('src'));
      content.querySelectorAll('script[src]').forEach(script => script.remove());
      content.querySelectorAll('[data-cc-animate]').forEach(el => el.removeAttribute('data-cc-animate'));
      // Product-only CSS is deliberately loaded only on this occasion page.
      mount.replaceChildren(document.importNode(content, true));
      for (const source of sources) await loadScript(source);
      document.dispatchEvent(new CustomEvent('shopify:section:load', {detail:{sectionId:root.dataset.giftingRenderSection}}));
      if (!new URLSearchParams(location.search).has('edit_gift')) restoreDraft(root);
      mount.hidden = false;
      status.hidden = true;
      document.querySelectorAll('[data-gifting-deferred]').forEach(block => { block.hidden = false; });
    } catch (error) {
      mount.replaceChildren(); message.textContent = error.message || 'Please refresh and try again.';
    } finally { root.removeAttribute('aria-busy'); }
  }
  function initAll() { document.querySelectorAll('[data-gifting-page]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
